import { Box } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useRef, useState } from 'react';
import Key from './Key';
import {
  COLS,
  GAP_FALLBACK_PX,
  GAP_FLEX_PX,
  PADDING_X,
  ROOT_SX_FALLBACK,
  ROOT_SX_WITH_GAP,
  ROW_SX_FALLBACK,
  ROW_SX_WITH_GAP,
  ROWS,
} from './NumberPad.styles';
import { checkIsWideFromEl, detectFlexGap, getWin } from './uiHelpers';

type NumberPadPropsT = {
  canClick: boolean;
  isRound: boolean;
  onClick: (char: string) => void;
};

type ResizeObserverT = new (cb: () => void) => {
  observe: (el: Element) => void;
  disconnect: () => void;
};

const keys = ('123456789*0#'.match(/.{3}/g) || []).map((group) =>
  group.split(''),
);

const NumberPad = observer(
  ({ canClick, isRound, onClick: handleClick }: NumberPadPropsT) => {
    const myRef = useRef<HTMLDivElement | null>(null);

    const [flexGapSupported, setFlexGapSupported] = useState(true);
    const [isWide, setIsWide] = useState(false);

    // 1) 시작하면 플렉스 갭이 사용가능한지 알아본다.
    useEffect(() => {
      const w = getWin();
      if (!w || typeof document === 'undefined' || !document.body) return;

      const ok = detectFlexGap();
      setFlexGapSupported(ok);
    }, []);

    // 2) 플렉스 갭 변경마다 updateIsWide 함수를 완성한다.
    const updateIsWide = useCallback(() => {
      const gapPx = flexGapSupported ? GAP_FLEX_PX : GAP_FALLBACK_PX;
      const next = checkIsWideFromEl({
        el: myRef.current,
        gapPx,
        cols: COLS,
        rows: ROWS,
      });
      if (next !== null) setIsWide(next);
    }, [flexGapSupported]);

    // 3) updateIsWide 함수가 변경될 때마다 실제로 넘버키가 가로형인지 세로형인지 판단한다
    useEffect(() => {
      const w = getWin();
      if (!w) return;

      updateIsWide(); // ✅ 항상 최신 flexGapSupported로 1회 측정

      const AnyResizeObserver = (w as any).ResizeObserver as
        | ResizeObserverT
        | undefined;
      if (!AnyResizeObserver) return;

      const ro = new AnyResizeObserver(() => updateIsWide());
      const el = myRef.current;
      if (el) ro.observe(el);

      return () => ro.disconnect();
    }, [updateIsWide]);

    const rootSx = flexGapSupported ? ROOT_SX_WITH_GAP : ROOT_SX_FALLBACK;
    const rowSx = flexGapSupported ? ROW_SX_WITH_GAP : ROW_SX_FALLBACK;

    return (
      <Box ref={myRef} px={PADDING_X} sx={rootSx}>
        {keys.map((row, rowIndex) => (
          <Box key={rowIndex} sx={rowSx}>
            {row.map((col) => (
              <Key
                key={col}
                char={col}
                isRound={isRound}
                isWide={isWide}
                canClick={canClick}
                onClick={() => canClick && handleClick(col)}
              />
            ))}
          </Box>
        ))}
      </Box>
    );
  },
);

export default NumberPad;
